import React, { useState, useEffect, useCallback, useMemo } from "react";
import { API_BASE_URL } from "../../config";
import { moneySubmittedState } from "../Management/SaleReportManager/moneySubmitted";

const SHIFT_ADMIN_API = `${API_BASE_URL}/saledata/api/truck-shifts/admin`;

// จัดการกะ (Vector 2026-09-26): rows บรรจุ/เพิ่ม that never got bound to a กะรถ — everything
// since the truck-shift deploy until route→uid binding existed — are fixed here by hand. Pick
// the สาย, pick the sale round (report code); the row takes that round's uid and กะ. Every
// report code has a copy button so it can be pasted into the dev pages.

const KIND_LABEL = { LOAD_OUT: "บรรจุ", REFILL: "เพิ่ม" };

function getToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || "";
    } catch { return ""; }
}

async function api(method, path, body) {
    const r = await fetch(`${SHIFT_ADMIN_API}${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(text || `${r.status} ${r.statusText}`);
    }
    return r.json();
}

function isoDate(d) {
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function shiftDays(iso, days) {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return isoDate(d);
}

function fmtTime(value) {
    if (!value) return "-";
    const s = String(value).replace("T", " ");
    return s.length > 16 ? s.slice(0, 16) : s;
}

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    }
}

function CopyCode({ code }) {
    const [done, setDone] = useState(false);
    return (
        <span style={{ whiteSpace: "nowrap" }}>
            <code>{code}</code>{" "}
            <button
                className="btc-btn btn-sm"
                style={{ backgroundColor: done ? "#6c757d" : "#343a40", padding: "1px 8px", fontSize: 12 }}
                onClick={async () => { if (await copyText(code)) { setDone(true); setTimeout(() => setDone(false), 1200); } }}
            >
                {done ? "คัดลอกแล้ว" : "คัดลอก"}
            </button>
        </span>
    );
}

const selectStyle = { padding: "4px 8px", borderRadius: 4, border: "1px solid #E5E5E5" };
const dateStyle = { ...selectStyle, width: "auto", display: "inline-block" };
// Full-width cards: the tables here are wider than btc-card's default width.
const cardStyle = { padding: "16px 24px", marginBottom: 16, width: "100%", maxWidth: "none", boxSizing: "border-box" };
const nowrap = { whiteSpace: "nowrap" };

export default function ShiftAdmin() {
    const today = isoDate(new Date());
    const [from, setFrom] = useState(shiftDays(today, -2));
    const [to, setTo] = useState(today);
    const [checkpoints, setCheckpoints] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [picks, setPicks] = useState({}); // "KIND-id" -> { sellerId, reportCode }
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [onlyUnbound, setOnlyUnbound] = useState(false);
    const [deleting, setDeleting] = useState(null); // checkpoint awaiting delete confirmation
    const [reason, setReason] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const load = useCallback(async () => {
        setError("");
        try {
            // Rounds one day either side: a load-out entered the evening before belongs to the next day's round.
            const [cps, rds] = await Promise.all([
                api("GET", `/checkpoints?from=${from}&to=${to}`),
                api("GET", `/rounds?from=${shiftDays(from, -1)}&to=${shiftDays(to, 1)}`),
            ]);
            setCheckpoints(cps);
            setRounds(rds);
            setPicks({});
        } catch (e) {
            setError(e.message || "โหลดไม่สำเร็จ");
        }
    }, [from, to]);

    useEffect(() => { load(); }, [load]);

    // One entry per seller account seen in the rounds — the สาย choices.
    const sellers = useMemo(() => {
        const map = new Map();
        rounds.forEach(r => { if (r.sellerId && !map.has(r.sellerId)) map.set(r.sellerId, r.sellerName || r.sellerId); });
        return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "th"));
    }, [rounds]);

    function defaultSeller(cp) {
        if (cp.sellerUid) return cp.sellerUid;
        const byName = sellers.find(s => s.name === cp.route);
        return byName ? byName.id : "";
    }

    function pickOf(cp) {
        const key = `${cp.kind}-${cp.id}`;
        const p = picks[key] || {};
        return {
            key,
            sellerId: p.sellerId !== undefined ? p.sellerId : defaultSeller(cp),
            reportCode: p.reportCode !== undefined ? p.reportCode : (cp.reportCodes && cp.reportCodes[0]) || "",
        };
    }

    function setPick(key, patch) {
        setPicks(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    }

    async function bind(cp, reportCode) {
        const key = `${cp.kind}-${cp.id}`;
        setBusy(key);
        setError("");
        try {
            const updated = await api("POST", "/bind", { kind: cp.kind, id: cp.id, reportCode: reportCode || "" });
            setCheckpoints(prev => prev.map(c => (c.kind === cp.kind && c.id === cp.id ? updated : c)));
            setPicks(prev => { const next = { ...prev }; delete next[key]; return next; });
            // A round from before the truck-shift deploy gains a shift on first bind — refresh rounds.
            setRounds(await api("GET", `/rounds?from=${shiftDays(from, -1)}&to=${shiftDays(to, 1)}`));
        } catch (e) {
            setError(e.message || "ผูกไม่สำเร็จ");
        } finally {
            setBusy("");
        }
    }

    function askDelete(cp) {
        setDeleting(cp);
        setReason("");
        setDeleteError("");
    }

    async function confirmDelete() {
        const cp = deleting;
        const key = `${cp.kind}-${cp.id}`;
        setBusy(key);
        setDeleteError("");
        try {
            await api("POST", "/delete", { kind: cp.kind, id: cp.id, reason: reason.trim() });
            setDeleting(null);
            await load();
        } catch (e) {
            setDeleteError(e.message || "ลบไม่สำเร็จ");
        } finally {
            setBusy("");
        }
    }

    const visible = onlyUnbound ? checkpoints.filter(c => !c.shiftId) : checkpoints;
    const unboundCount = checkpoints.filter(c => !c.shiftId).length;

    return (
        <div className="btc-container">
            <h1>จัดการกะ — ผูกรอบบรรจุ/เพิ่ม เข้ากับรอบขาย</h1>

            <div className="btc-card" style={cardStyle}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span>ตั้งแต่</span>
                    <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={dateStyle} />
                    <span>ถึง</span>
                    <input type="date" value={to} onChange={e => setTo(e.target.value)} style={dateStyle} />
                    <button className="btc-btn btn-sm" style={{ backgroundColor: "#343a40" }} onClick={load}>โหลด</button>
                    <label style={{ marginLeft: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={onlyUnbound} onChange={e => setOnlyUnbound(e.target.checked)} style={{ width: "auto" }} />{" "}
                        เฉพาะที่ยังไม่มีกะ ({unboundCount})
                    </label>
                </div>
                {error && <p className="error" style={{ marginBottom: 0 }}>{error}</p>}
            </div>

            <div className="btc-card" style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>รอบบรรจุ / เพิ่ม — {visible.length} รายการ</h3>
                {visible.length === 0
                    ? <p>ไม่มีรายการในช่วงนี้</p>
                    : (
                        <div style={{ overflowX: "auto" }}>
                        <table className="btc-table" style={{ width: "100%", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th>ประเภท</th>
                                    <th>เวลาบันทึก</th>
                                    <th>วันที่</th>
                                    <th style={{ textAlign: "left" }}>สาย</th>
                                    <th>หลอด</th>
                                    <th>บด</th>
                                    <th>กะ</th>
                                    <th style={{ textAlign: "left" }}>รอบขายที่ผูกอยู่</th>
                                    <th style={{ textAlign: "left" }}>เลือกสาย</th>
                                    <th style={{ textAlign: "left" }}>เลือก report code</th>
                                    <th style={{ width: 200 }}>จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(cp => {
                                    const p = pickOf(cp);
                                    const options = rounds.filter(r => r.sellerId === p.sellerId);
                                    const current = (cp.reportCodes && cp.reportCodes[0]) || "";
                                    return (
                                        <tr key={p.key} style={cp.shiftId ? undefined : { background: "#fff4f4" }}>
                                            <td>{KIND_LABEL[cp.kind] || cp.kind}</td>
                                            <td style={nowrap}>{fmtTime(cp.createdAt)}</td>
                                            <td style={nowrap}>{cp.businessDate}</td>
                                            <td style={{ textAlign: "left", ...nowrap }}>{cp.route}</td>
                                            <td>{cp.tubeQty ?? 0}</td>
                                            <td>{cp.crushedQty ?? 0}</td>
                                            <td>{cp.shiftId ?? "-"}</td>
                                            <td style={{ textAlign: "left" }}>
                                                {cp.reportCodes && cp.reportCodes.length > 0
                                                    ? cp.reportCodes.map(code => <div key={code}><CopyCode code={code} /></div>)
                                                    : (cp.shiftId ? "(กะยังไม่มีรอบขาย)" : "-")}
                                            </td>
                                            <td style={{ textAlign: "left" }}>
                                                <select
                                                    value={p.sellerId}
                                                    onChange={e => setPick(p.key, { sellerId: e.target.value, reportCode: "" })}
                                                    style={selectStyle}
                                                >
                                                    <option value="">-- เลือกสาย --</option>
                                                    {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ textAlign: "left" }}>
                                                <select
                                                    value={p.reportCode}
                                                    onChange={e => setPick(p.key, { reportCode: e.target.value })}
                                                    style={{ ...selectStyle, minWidth: 220 }}
                                                    disabled={!p.sellerId}
                                                >
                                                    <option value="">-- เลือกรอบขาย --</option>
                                                    {options.map(r => (
                                                        <option key={r.reportCode} value={r.reportCode}>
                                                            {r.reportCode} · {r.date} รอบ {r.round ?? "-"}{r.shiftId ? ` · กะ ${r.shiftId}` : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button
                                                    className="btc-btn btn-sm"
                                                    style={{ backgroundColor: "#343a40", marginRight: 6 }}
                                                    disabled={!p.reportCode || p.reportCode === current || busy === p.key}
                                                    onClick={() => bind(cp, p.reportCode)}
                                                >
                                                    {busy === p.key ? "..." : "ผูก"}
                                                </button>
                                                <button
                                                    className="btc-btn btn-sm"
                                                    style={{ backgroundColor: "#c0392b", marginRight: 6 }}
                                                    disabled={busy === p.key}
                                                    onClick={() => askDelete(cp)}
                                                >
                                                    ลบ
                                                </button>
                                                {cp.shiftId && (
                                                    <button
                                                        className="btc-btn btn-sm"
                                                        style={{ backgroundColor: "#6B6B6B" }}
                                                        disabled={busy === p.key}
                                                        onClick={() => { if (window.confirm("ยกเลิกการผูกกะของแถวนี้?")) bind(cp, ""); }}
                                                    >
                                                        ยกเลิกผูก
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    )
                }
            </div>

            <div className="btc-card" style={cardStyle}>
                <h3 style={{ marginTop: 0 }}>รอบขาย (report code) — {rounds.length} รายการ</h3>
                {rounds.length === 0
                    ? <p>ไม่มีรอบขายในช่วงนี้</p>
                    : (
                        <div style={{ overflowX: "auto" }}>
                        <table className="btc-table" style={{ width: "100%", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: "left" }}>report code</th>
                                    <th style={{ textAlign: "left" }}>สาย</th>
                                    <th>วันที่</th>
                                    <th>รอบ</th>
                                    <th>ส่งแรก</th>
                                    <th>ส่งสุดท้าย</th>
                                    <th>ส่งยอดแล้ว</th>
                                    <th>กะ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rounds.map(r => (
                                    <tr key={r.reportCode}>
                                        <td style={{ textAlign: "left" }}><CopyCode code={r.reportCode} /></td>
                                        <td style={{ textAlign: "left", ...nowrap }}>{r.sellerName || r.sellerId}</td>
                                        <td style={nowrap}>{r.date}</td>
                                        <td>{r.round ?? "-"}</td>
                                        <td>{r.firstDelivery ? String(r.firstDelivery).slice(0, 5) : "-"}</td>
                                        <td>{r.lastDelivery ? String(r.lastDelivery).slice(0, 5) : "-"}</td>
                                        <td title={moneySubmittedState(r) === null ? "ไม่ทราบสถานะ" : undefined}>{moneySubmittedState(r) === null ? "?" : moneySubmittedState(r) ? "✓" : "-"}</td>
                                        <td>{r.shiftId ?? "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )
                }
            </div>
            {deleting && (
                <div role="dialog" aria-label="ยืนยันลบรายการ" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div className="btc-card" style={{ background: "#fff", padding: 24, minWidth: 360, maxWidth: 480 }}>
                        <h3 style={{ marginTop: 0, color: "#c0392b" }}>ลบรายการ{KIND_LABEL[deleting.kind] || deleting.kind}นี้?</h3>
                        <table style={{ fontSize: 14, marginBottom: 12 }}>
                            <tbody>
                                <tr><td>สาย</td><td>{deleting.route}</td></tr>
                                <tr><td>รอบ/กะ</td><td>{deleting.reportCodes && deleting.reportCodes.length > 0 ? deleting.reportCodes.join(", ") : "-"} / กะ {deleting.shiftId ?? "-"}</td></tr>
                                <tr><td>วัน-เวลา</td><td>{deleting.businessDate} {fmtTime(deleting.createdAt)}</td></tr>
                                <tr><td>ชนิด</td><td>{KIND_LABEL[deleting.kind] || deleting.kind}</td></tr>
                                <tr><td>จำนวนกระสอบ</td><td>หลอด {deleting.tubeQty ?? 0} · บด {deleting.crushedQty ?? 0}</td></tr>
                            </tbody>
                        </table>
                        <label style={{ display: "block", marginBottom: 4 }}>เหตุผลที่ลบ (บังคับ)</label>
                        <textarea
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            rows={3}
                            style={{ width: "100%", boxSizing: "border-box", ...selectStyle }}
                            placeholder="เช่น กดบันทึกซ้ำ"
                        />
                        <p style={{ fontSize: 12, color: "#6B6B6B", margin: "8px 0" }}>ระบบเก็บสำเนาแถวที่ลบไว้ในประวัติการลบ กู้ย้อนได้</p>
                        {deleteError && <p className="error">{deleteError}</p>}
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button className="btc-btn btn-sm" style={{ backgroundColor: "#6B6B6B" }} onClick={() => setDeleting(null)}>ยกเลิก</button>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: "#c0392b" }}
                                disabled={!reason.trim() || busy === `${deleting.kind}-${deleting.id}`}
                                onClick={confirmDelete}
                            >
                                ยืนยันลบ
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
